import { useState } from 'react';

import { Grid, Button, Heading, Text, Box } from '@simplywallst/ui-core';

import { useCloudflareImage } from '@/hooks/useCloudflareImage';

import { LearningHubActionSheet } from './LearningHubActionSheet';
import { useLearningHub } from './useLearningHub';

import { Image, ImageButton } from './styled';
import { useTracking } from 'react-tracking';
import { type HeadingElements } from '@/utilities/heading';

type Props = {
  contentKey: string;
  onClose?(): void;
  titleElement?: HeadingElements;
};

const IMAGE_WIDTH = 144;

export const LearningTile = ({
  contentKey,
  onClose,
  titleElement = 'h4',
}: Props) => {
  const { data, status } = useLearningHub({
    contentKey,
  });

  const { trackEvent } = useTracking();
  const [open, setOpen] = useState(false);

  const src = useCloudflareImage({
    src: data?.imageUrl || '',
    width: IMAGE_WIDTH * 2, // Higher DPI for retina screens
  });

  if (status !== 'success') return null;
  return (
    <Box
      marginTop="x3"
      marginBottom="x3"
      data-cy-id={`learning-tile-${contentKey}`}
    >
      <Grid
        paddingTop="x2"
        padding="x2"
        templateColumns="auto max-content"
        gap="18px"
        borderRadius="x1"
        backgroundColor="spaceGray"
      >
        <Grid
          paddingTop="x0_75"
          templateColumnsSm={`${IMAGE_WIDTH}px auto`}
          gap="18px"
        >
          <Grid maxWidth={`${IMAGE_WIDTH}px`} alignContent="start">
            <ImageButton onClick={() => setOpen(true)}>
              <Image src={src} />
            </ImageButton>
          </Grid>

          <Grid alignContent="start" gap="8px">
            <Heading as={titleElement} typography="smallStrong">
              {data.title}
            </Heading>
            <Text typography="xSmall">{data.description}</Text>
            <Grid
              justifyContent="start"
              paddingTop="x2"
              paddingTopSm="x0"
              gap="8px"
              templateColumns="auto auto"
            >
              <Button
                styleType="louder"
                onClick={() => {
                  trackEvent({
                    modifier: 'learning_hub_learn_more',
                    action: 'click',
                  });
                  setOpen(true);
                }}
              >
                {data.iframeCta}
              </Button>
              <Button
                type="button"
                styleType="gentle"
                onClick={() => {
                  trackEvent({
                    modifier: 'learning_hub_dismiss',
                    action: 'click',
                  });
                  onClose?.();
                }}
              >
                Dismiss
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {open && (
        <LearningHubActionSheet
          src={data.iframeUrl}
          iframeTitle={data.title}
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};
